import { Button, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import { useFilePreview } from "components/preview";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  useDeleteSecureFileMutation,
  useGetSecureFileAccessUrlMutation,
  usePurgeSecureFilesMutation,
  useRestoreSecureFileMutation,
} from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

import FileDetailsDialog from "./FileDetailsDialog";
import MoveFilesDialog, { type MoveRequest } from "./MoveFilesDialog";
import { previewRefusal, restoreAudience, type FileAction } from "./fileModel";

const MOVES: Partial<Record<FileAction, Omit<MoveRequest, "files">>> = {
  add_to_project: { mode: "project", title: "Add to Project" },
  move_to_project: { mode: "project", title: "Move to Project" },
  add_to_collection: { mode: "collection", title: "Add to Collection" },
  move_file: { mode: "collection", title: "Move File" },
};

/** What a file row's menu does, and the dialogs it opens. `onChanged` hears of
 * files that left the list through a row (deleted, moved, restored or deleted
 * for good). */
export function useFileActions({
  onChanged,
}: {
  onChanged?: (ids: number[]) => void;
} = {}) {
  const dispatch = useDispatch();
  const { openInlinePreview } = useFilePreview();
  const [getAccessUrl] = useGetSecureFileAccessUrlMutation();
  const [deleteFile, { isLoading: deleting }] = useDeleteSecureFileMutation();
  const [details, setDetails] = useState<SecureFile | null>(null);
  const [toDelete, setToDelete] = useState<SecureFile | null>(null);
  const [toRestore, setToRestore] = useState<SecureFile | null>(null);
  const [toPurge, setToPurge] = useState<SecureFile | null>(null);
  const [toMove, setToMove] = useState<{
    request: MoveRequest;
    clientId: number;
  } | null>(null);
  const [restoreFile, { isLoading: restoring }] =
    useRestoreSecureFileMutation();
  const [purgeFiles, { isLoading: purging }] = usePurgeSecureFilesMutation();

  const toast = (message: string, severity: "success" | "error" | "warning") =>
    dispatch(pushNotification({ message, severity }));

  // One preview at a time: a second click would read the file again.
  const previewing = useRef(false);
  const preview = async (file: SecureFile) => {
    const refusal = previewRefusal(file);
    if (refusal) {
      toast(refusal, "warning");
      return;
    }
    if (previewing.current) return;
    previewing.current = true;
    try {
      const { url, file_name } = await getAccessUrl({
        id: file.id,
        mode: "preview",
      }).unwrap();
      const blob = await (await fetch(url)).blob();
      openInlinePreview(file_name, blob, { size: file.size });
    } catch {
      toast("The file could not be previewed.", "error");
    } finally {
      previewing.current = false;
    }
  };

  const download = async (file: SecureFile) => {
    try {
      const { url } = await getAccessUrl({ id: file.id }).unwrap();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast("The file could not be downloaded.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteFile(toDelete.id).unwrap();
      toast("File deleted.", "success");
      onChanged?.([toDelete.id]);
      setToDelete(null);
    } catch (error) {
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "The file could not be deleted.",
        ),
        "error",
      );
    }
  };

  const confirmRestore = async () => {
    if (!toRestore) return;
    try {
      await restoreFile(toRestore.id).unwrap();
      toast(
        `File restored to ${toRestore.restore_to?.label ?? "its place"}.`,
        "success",
      );
      onChanged?.([toRestore.id]);
      setToRestore(null);
    } catch (error) {
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "The file could not be restored.",
        ),
        "error",
      );
    }
  };

  const confirmPurge = async () => {
    if (!toPurge) return;
    try {
      await purgeFiles([toPurge.id]).unwrap();
      toast("File deleted permanently.", "success");
      onChanged?.([toPurge.id]);
      setToPurge(null);
    } catch (error) {
      setToPurge(null);
      toast(
        (error as { status?: number })?.status === 404
          ? "This file is no longer in Deleted Files."
          : formatApiError(
              (error as { data?: unknown })?.data,
              "The file could not be deleted.",
            ),
        "error",
      );
    }
  };

  const target = toRestore?.restore_to;

  const run = (action: FileAction, file: SecureFile) => {
    const moving = MOVES[action];
    if (action === "details") setDetails(file);
    else if (action === "preview") void preview(file);
    else if (action === "download") void download(file);
    else if (action === "delete") setToDelete(file);
    else if (action === "restore") setToRestore(file);
    else if (action === "purge") setToPurge(file);
    else if (moving)
      setToMove({
        request: { ...moving, files: [file] },
        clientId: file.client_id ?? 0,
      });
  };

  const dialogs = (
    <>
      <FileDetailsDialog file={details} onClose={() => setDetails(null)} />
      <MoveFilesDialog
        request={toMove?.request ?? null}
        clientId={toMove?.clientId ?? 0}
        onClose={() => setToMove(null)}
        onMoved={(ids) => onChanged?.(ids)}
      />
      <ConfirmDialog
        open={toPurge !== null}
        title="Delete Permanently"
        message={
          <Typography variant="body2">
            <b>{toPurge?.file_name}</b> will be deleted for good. It cannot be
            restored.
          </Typography>
        }
        confirmLabel="Delete Permanently"
        onConfirm={confirmPurge}
        onCancel={() => setToPurge(null)}
        loading={purging}
      />
      <ConfirmDialog
        open={toDelete !== null}
        title="Delete File"
        message={
          <Typography variant="body2">
            <b>{toDelete?.file_name}</b> will be removed for everyone who can
            see it. An Admin can restore it from Deleted Files.
          </Typography>
        }
        confirmLabel="Delete File"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
        loading={deleting}
      />
      {target?.refusal ? (
        <AppDialog
          open
          onClose={() => setToRestore(null)}
          title="Restore File"
          maxWidth="xs"
          actions={
            <Button variant="outlined" onClick={() => setToRestore(null)}>
              Close
            </Button>
          }
        >
          <Typography variant="body2">
            <b>{toRestore?.file_name}</b> belongs to a closed project, where
            nothing changes. {target.refusal}
          </Typography>
        </AppDialog>
      ) : (
        <ConfirmDialog
          open={toRestore !== null}
          title="Restore File"
          message={
            <>
              <Typography variant="body2">
                <b>{toRestore?.file_name}</b> goes back to{" "}
                <b>{target?.label}</b>.
              </Typography>
              {toRestore && restoreAudience(toRestore) ? (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {restoreAudience(toRestore)}
                </Typography>
              ) : null}
            </>
          }
          confirmLabel="Restore"
          onConfirm={confirmRestore}
          onCancel={() => setToRestore(null)}
          loading={restoring}
        />
      )}
    </>
  );

  return { run, dialogs, download };
}
